import { useEffect, useMemo, useState } from "react";
import { Search, ExternalLink, ArrowRight, Sparkles, Info, Phone, MapPin, Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function CommunityResources() {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [CATEGORIES, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/community-resources");
        if (!alive) return;
        setCategories(r.data || []);
        if (r.data && r.data.length) setActiveId(r.data[0].id);
      } catch {
        // intentionally ignored — page falls back to empty state
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES
      .map((c) => ({
        ...c,
        items: (c.items || []).filter((it) =>
          (it.title || "").toLowerCase().includes(q) ||
          (it.desc || "").toLowerCase().includes(q) ||
          c.title.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [query, CATEGORIES]);

  const totalLinks = useMemo(() => CATEGORIES.reduce((s, c) => s + (c.items?.length || 0), 0), [CATEGORIES]);
  const visibleLinks = useMemo(() => filtered.reduce((s, c) => s + c.items.length, 0), [filtered]);

  // Active category for the "browse by category" view (only when no search)
  const activeCat = !query ? CATEGORIES.find((c) => c.id === activeId) || CATEGORIES[0] : null;

  return (
    <div data-testid="community-resources-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-sand">
        <div className="absolute inset-0 bg-navy">
          <div className="absolute inset-0 pattern-girih opacity-25" />
          <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 text-cream">
          <div className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold inline-flex items-center gap-2">
            <Sparkles size={12} /> Community Outreach
          </div>
          <h1 className="display text-4xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl">
            Community<br /><span className="text-gold">Resources</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-cream/80 max-w-3xl leading-relaxed font-serif">
            A curated directory of trusted local, state, and federal resources for our neighbors —
            from healthcare and housing to seniors, mental health, and immigration. Click any link to learn more.
          </p>

          {/* Search */}
          <div className="mt-9 max-w-2xl relative">
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-cream/50" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="resources-search"
              placeholder="Search resources… (e.g. food, mental health, rent)"
              className="w-full pl-14 pr-5 py-4 rounded-full bg-cream/10 backdrop-blur-md border border-cream/20 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold/60 focus:bg-cream/15 transition-all"
            />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-cream/65">
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> {CATEGORIES.length} categories</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> {visibleLinks} of {totalLinks} resources {query && "matched"}</span>
          </div>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8 md:h-12 text-cream" preserveAspectRatio="none" viewBox="0 0 1440 60">
          <path fill="currentColor" d="M0,30 C320,60 1120,0 1440,30 L1440,60 L0,60 Z" />
        </svg>
      </section>

      {/* DISCLAIMER */}
      <section className="px-6 md:px-10 pt-10 bg-cream">
        <div className="max-w-5xl mx-auto rounded-2xl bg-amber-50/60 border border-amber-200/60 p-5 md:p-6 flex gap-4 items-start">
          <Info className="text-amber-700 shrink-0 mt-0.5" size={20} />
          <p className="text-sm text-amber-900/85 leading-relaxed">
            The resources and services listed are <strong>for informational purposes only</strong>.
            Click on the links provided to learn more about various non-profit, local, state, and federal resources.
            Users are responsible for checking the content and accuracy of the information.
            The Flint Islamic Center does not endorse or recommend, and is not liable for, claims and/or damages
            that may come from the information provided.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="px-6 md:px-10 py-14 md:py-20 bg-cream">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-ink/50">
              <Loader2 className="animate-spin" size={32} />
              <p className="mt-4 text-sm">Loading resources…</p>
            </div>
          ) : CATEGORIES.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-5xl mb-3">📭</div>
              <h3 className="font-serif text-2xl text-ink">No resources yet</h3>
              <p className="text-ink/60 mt-2">Resources will appear here once published.</p>
            </div>
          ) : query ? (
            // --------- SEARCH RESULTS ---------
            <div className="space-y-10">
              {filtered.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-5xl mb-3">🔎</div>
                  <h3 className="font-serif text-2xl text-ink">No matches for "{query}"</h3>
                  <p className="text-ink/60 mt-2">Try a different keyword like "food", "rent", or "mental health".</p>
                </div>
              ) : (
                filtered.map((cat) => (
                  <div key={cat.id} data-testid={`resource-cat-${cat.id}`}>
                    <div className="flex items-baseline gap-3 mb-5">
                      <span className="text-2xl">{cat.icon}</span>
                      <h2 className="font-serif text-2xl md:text-3xl text-ink">{cat.title}</h2>
                      <span className="text-xs text-ink/50">· {cat.items.length}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {cat.items.map((it, i) => <ResourceCard key={i} item={it} />)}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // --------- CATEGORY BROWSE ---------
            <div className="grid lg:grid-cols-[280px,1fr] gap-8">
              {/* Sidebar (sticky on desktop) */}
              <aside className="lg:sticky lg:top-24 lg:self-start">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-3">Browse by category</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-1.5" data-testid="resource-nav">
                  {CATEGORIES.map((c) => {
                    const active = c.id === activeId;
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          setActiveId(c.id);
                          // scroll up on mobile / tablet
                          if (window.innerWidth < 1024) {
                            document.getElementById("resource-content")?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }}
                        data-testid={`resource-nav-${c.id}`}
                        className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                          active
                            ? "bg-navy text-cream shadow-md"
                            : "bg-white border border-sand text-ink/75 hover:border-navy/30 hover:text-navy"
                        }`}
                      >
                        <span className="text-lg leading-none">{c.icon}</span>
                        <span className="flex-1 font-medium leading-tight">{c.title}</span>
                        <span className={`text-[10px] ${active ? "text-gold" : "text-ink/35"}`}>{c.items.length}</span>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* Active category panel */}
              <div id="resource-content" className="min-w-0">
                {activeCat && (
                  <div data-testid={`resource-cat-${activeCat.id}`}>
                    <div className="rounded-3xl bg-gradient-to-br from-navy to-navy-900 text-cream p-7 md:p-9 mb-7 relative overflow-hidden">
                      <div className="absolute inset-0 pattern-girih opacity-15" />
                      <div className="relative flex items-start gap-4">
                        <span className="text-4xl md:text-5xl leading-none">{activeCat.icon}</span>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Category</div>
                          <h2 className="font-serif text-3xl md:text-4xl mt-2">{activeCat.title}</h2>
                          <div className="text-cream/70 text-sm mt-2">{activeCat.items.length} resource{activeCat.items.length === 1 ? "" : "s"}</div>
                        </div>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {activeCat.items.map((it, i) => <ResourceCard key={i} item={it} />)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-navy text-cream relative overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-10" />
        <div className="relative max-w-5xl mx-auto grid md:grid-cols-3 gap-6 text-sm">
          <div className="flex gap-3">
            <MapPin className="text-gold shrink-0" size={18} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Visit us</div>
              <div className="mt-1 leading-relaxed text-cream/85">9447 Corunna Rd<br />Swartz Creek, MI 48473</div>
            </div>
          </div>
          <div className="flex gap-3">
            <Phone className="text-gold shrink-0" size={18} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Call us</div>
              <a href="tel:+18104062617" className="mt-1 block hover:text-gold">(810) 406-2617</a>
            </div>
          </div>
          <div className="flex gap-3">
            <Mail className="text-gold shrink-0" size={18} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Email</div>
              <a href="mailto:flintislamiccenter@gmail.com" className="mt-1 block hover:text-gold break-all">flintislamiccenter@gmail.com</a>
            </div>
          </div>
        </div>
        <div className="relative max-w-5xl mx-auto mt-10 text-center text-cream/65 text-xs">
          Don't see what you need? <a href="/contact" className="text-gold underline-offset-4 hover:underline">Contact us</a> — we'll do our best to help.
        </div>
      </section>
    </div>
  );
}

function ResourceCard({ item }) {
  const hasUrl = !!item.url;
  const hasSub = item.sub && item.sub.length > 0;
  // Use a wrapper <div> with a single inner main link when there are sub-links,
  // to avoid nesting <a> tags (invalid HTML).
  const useDivWrapper = hasUrl && hasSub;

  if (useDivWrapper) {
    return (
      <div className="group block rounded-2xl bg-white border border-sand p-5 transition-all hover:border-gold/50 hover:shadow-xl hover:shadow-navy/5 hover:-translate-y-0.5">
        <a href={item.url} target="_blank" rel="noreferrer noopener" className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-lg text-ink leading-snug group-hover:text-navy">{item.title}</h3>
          <ExternalLink size={14} className="text-ink/30 group-hover:text-gold shrink-0 mt-1" />
        </a>
        {item.desc && <p className="mt-2 text-sm text-ink/65 leading-relaxed">{item.desc}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {item.sub.map((s, i) => (
            <a
              key={i}
              href={s.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs text-navy bg-gold/15 hover:bg-gold/25 px-3 py-1.5 rounded-full font-medium"
            >
              {s.label} <ArrowRight size={10} />
            </a>
          ))}
        </div>
      </div>
    );
  }

  const Tag = hasUrl ? "a" : "div";
  const linkProps = hasUrl ? { href: item.url, target: "_blank", rel: "noreferrer noopener" } : {};
  return (
    <Tag
      {...linkProps}
      className={`group block rounded-2xl bg-white border border-sand p-5 transition-all ${
        hasUrl ? "hover:border-gold/50 hover:shadow-xl hover:shadow-navy/5 hover:-translate-y-0.5" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-lg text-ink leading-snug">{item.title}</h3>
        {hasUrl && <ExternalLink size={14} className="text-ink/30 group-hover:text-gold shrink-0 mt-1" />}
      </div>
      {item.desc && <p className="mt-2 text-sm text-ink/65 leading-relaxed">{item.desc}</p>}
    </Tag>
  );
}
